// DEMO ONLY. The answer chats (Figma 6410:9002, 6424:11520, 6423:10977, 6423:11257) as page
// response blocks: the payloads a live answer would carry.
import {
  GovernedAnalysisBlock,
  MultiAnswerPanelBlock,
  ReportGeneratedBlock,
  WeeklyUpdateReportBlock
} from '../../support/types';
import { IOrbitPageDemoChat, seedTurn } from './seedTurn';

export const CHART_QUESTION = 'Show customer value distribution';

const CHART_CHIPS = ['Screen: Customer Insights', 'Region: All', 'Period: Last 30 Days'];

/** 6424:11849. The Growth Planner demos open on the same answer. */
export const GOVERNED_ANALYSIS_SEED: GovernedAnalysisBlock = {
  type: 'governed_analysis',
  id: 'customer-value-distribution',
  eyebrow: 'GOVERNED ANALYSIS',
  icon: 'chart',
  text: 'Based on the current fiscal quarter, the customer value distribution is heavily weighted towards the Premier segment, which accounts for 45% of total value despite representing only 18% of the customer base. The At-Risk segment has grown by 3.2% since last month.',
  highlights: ['Premier', 'At-Risk'],
  chart: {
    kind: 'donut',
    total: { label: 'TOTAL', value: '$4.2B' },
    segments: [
      { key: 'premier', label: 'Premier', value: '45%', share: 45, tone: 'accent' },
      { key: 'growth', label: 'Growth', value: '30%', share: 30, tone: 'info' },
      // FLAG: 6424:11881 paints At-Risk #101830, the panel's own fill, so it is invisible. Sent as
      // `error` until the designer confirms.
      { key: 'at_risk', label: 'At-Risk', value: '15%', share: 15, tone: 'error' },
      { key: 'new', label: 'New', value: '10%', share: 10, tone: 'neutral' }
    ],
    actions: [
      { id: 'download_chart', label: 'Download chart', kind: 'secondary', icon: 'download' },
      { id: 'expand_chart', label: 'Expand chart', kind: 'secondary', icon: 'expand' }
    ]
  },
  confidence: {
    level: 'high',
    label: 'High confidence · 82%',
    reason_label: 'Reason:',
    reason:
      'Governed metrics from the certified customer value model, refreshed 06 Sep 2026. All four segments reconcile to the finance ledger.'
  },
  actions: [{ id: 'send_to_growth_planner', label: 'Send to Growth Planner', kind: 'primary' }]
};

const REPORT_GENERATED: ReportGeneratedBlock = {
  type: 'report_generated',
  id: 'customer-engagement-report',
  tone: 'success',
  title: 'Customer Engagement report is generated successfully.',
  body: 'Download or send the generated report via email from the Report module.',
  checks: [
    'Total 3000 customers were identified.',
    '2 active campaigns were found for the eligible customers.'
  ],
  actions: [
    {
      id: 'view_generated_report',
      label: 'View Generated Report',
      kind: 'primary',
      payload: { report_download_id: null }
    }
  ]
};

const MULTI_ANSWER: MultiAnswerPanelBlock = {
  type: 'multi_answer_panel',
  id: 'three-requests',
  intro:
    'I found 3 separate requests. Orbit returns each answer in its own row so the results remain easy to scan.',
  intro_highlights: ['3 separate requests'],
  items: [
    {
      id: 'subscribed-features',
      label: 'Answer 1 of 3',
      title: 'Subscribed feature list',
      body: 'Intelligence Suite, Customer 360, Campaigns, Rewards, Loyalty and Perx Orbit are enabled.',
      actions: [{ id: 'download_csv', label: 'Download CSV', kind: 'link', icon: 'download' }]
    },
    {
      id: 'whats-new',
      label: 'Answer 2 of 3',
      title: "What's new in Perx",
      body: 'Latest release highlights include new Layer 6 controls, Orbit support workflows and dashboard improvements.',
      actions: [
        {
          id: 'download_csv_excel',
          label: 'Download CSV / Excel',
          kind: 'link',
          icon: 'download'
        }
      ]
    },
    {
      id: 'stamp-campaign-kb',
      label: 'Answer 3 of 3',
      title: 'How to create a stamp campaign',
      body: 'I found a matching Knowledge Base article in the Perx Support Portal.',
      actions: [{ id: 'open_kb', label: 'Open KB article', kind: 'link', icon: 'open' }]
    }
  ]
};

const WEEKLY_UPDATE: WeeklyUpdateReportBlock = {
  type: 'weekly_update_report',
  id: 'weekly-update-2026-09-08',
  intro: 'Here is the latest weekly update.',
  title: 'Weekly Update',
  subtitle: 'Week of 08 Sep 2026',
  head_action: {
    id: 'open_weekly_update',
    label: 'Open weekly update',
    kind: 'secondary',
    icon: 'open'
  },
  rows: [
    { label: 'Reporting Date', value: '08 Sep 2026' },
    { label: 'Project Manager', value: 'Sarah Mitchell' }
  ],
  sections: [
    {
      id: 'summary',
      title: 'Summary',
      expanded: true,
      body: 'Overall platform performance remained stable. Two new campaigns were launched and adoption increased by 12%.'
    },
    {
      id: 'highlights',
      title: 'Strategic Highlights',
      expanded: true,
      items: [
        '2 new campaigns launched',
        'User adoption increased by 12%',
        'No major incidents this week'
      ]
    },
    { id: 'resolved', title: 'Resolved Issues', count: 3, expanded: false },
    { id: 'watch', title: 'Open Watch Items', count: 2, expanded: false }
  ],
  actions: [
    {
      id: 'previous_updates',
      label: 'Previous Updates',
      kind: 'secondary',
      // 6265:4825 draws a plain clock.
      icon: 'clock'
    }
  ]
};

/**
 * The per-type seeds by name. `showcaseSeed.ts` composes the Pinned test chat from them, and the
 * hand-off demos open on `governedAnalysis`, so an edit here shows there too: keep the names.
 */
export const ANSWER_SEEDS = {
  chartQuestion: CHART_QUESTION,
  governedAnalysis: GOVERNED_ANALYSIS_SEED,
  reportQuestion:
    'Generate customer engagement report of last 30 days for financially stressed customers',
  reportGenerated: REPORT_GENERATED,
  multiQuestion:
    'Show me our subscribed features, latest release updates, and how to create a stamp campaign.',
  multiAnswer: MULTI_ANSWER,
  weeklyQuestion: 'What is the latest weekly update for our account?',
  weeklyUpdate: WEEKLY_UPDATE
};

export const ANSWER_DEMO_CHATS: IOrbitPageDemoChat[] = [
  {
    id: 'chat-with-chart',
    title: 'Customer value distribution',
    chips: CHART_CHIPS,
    turns: [seedTurn(CHART_QUESTION, [GOVERNED_ANALYSIS_SEED])]
  },
  {
    id: 'report-generation',
    title: 'Report Generation',
    chips: CHART_CHIPS,
    turns: [
      seedTurn(CHART_QUESTION, [GOVERNED_ANALYSIS_SEED]),
      seedTurn(ANSWER_SEEDS.reportQuestion, [REPORT_GENERATED])
    ]
  },
  {
    id: 'multi-question',
    title: 'Multi-question chat conversation',
    chips: ['Screen: Customer Insights', 'Period: Last 30 Days'],
    turns: [seedTurn(ANSWER_SEEDS.multiQuestion, [MULTI_ANSWER])]
  },
  {
    id: 'weekly-updates',
    title: 'Weekly updates',
    chips: ['Screen: Business Overview', 'Period: Latest'],
    turns: [seedTurn(ANSWER_SEEDS.weeklyQuestion, [WEEKLY_UPDATE])]
  }
];
